import {Version} from '../redux/config';

export const ANONYMOUS_USER = '_anonymous';
export const MAX_ENTRIES = 1500;
export const MAX_ENTRY_CHARS = 8 * 1024;
export const MAX_USER_CHARS = 2 * 1024 * 1024;
export const MAX_STORED_USERS = 5;
export const MAX_PINNED_ENTRIES = 5;

const STORAGE_PREFIX = 'callcenter:uconsole:';
const INDEX_KEY = 'callcenter:uconsole:index';
const PERSIST_DEBOUNCE_MS = 1200;
const SENSITIVE_KEY = /jwt|token|password|passwd|^pass$|credential|authorization|secret|hash/i;
const SENSITIVE_INLINE = /(access_token=)[^&\s"]+|(Bearer\s+)[^\s"]+/gi;

export type UserConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export type UserConsoleChannel = 'logout' | 'request' | 'action' | 'hub' | 'sip' | 'js' | 'connection' | 'console';

export type UserConsoleEntry = {
    ts: string;
    level: UserConsoleLevel;
    message: string;
    channel?: UserConsoleChannel;
    pinned?: boolean;
    group?: string;
};

export type UserConsoleDumpFormat = 'channels' | 'time';

export type UserConsoleDump = {
    userName: string;
    exportedAt: string;
    appVersion: string;
    href: string;
    userAgent: string;
    pinned: UserConsoleEntry[];
    entries: UserConsoleEntry[];
    truncated: boolean;
};

type UserBucket = {
    userName: string;
    entries: UserConsoleEntry[];
    pinned: UserConsoleEntry[];
    chars: number;
};

type ConsoleMethod = UserConsoleLevel;

const CONSOLE_METHODS: ConsoleMethod[] = ['log', 'info', 'warn', 'error', 'debug'];
const CONSOLE_CHANNELS: UserConsoleChannel[] = ['logout', 'request', 'action', 'hub', 'sip', 'js', 'connection', 'console'];

const CHANNEL_SECTIONS: {id: UserConsoleChannel; title: string}[] = [
    {id: 'logout', title: 'LOGOUT'},
    {id: 'request', title: 'REQUESTS'},
    {id: 'action', title: 'ACTIONS  |  user / UI'},
    {id: 'js', title: 'JS ERRORS  |  unhandled'},
    {id: 'hub', title: 'HUBS  |  SignalR'},
    {id: 'sip', title: 'SIP / ICE / WebRTC'},
    {id: 'connection', title: 'CONNECTION  |  newMessage'},
    {id: 'console', title: 'CONSOLE'}
];

export function classifyUserConsoleChannel(message: string): UserConsoleChannel {
    const text = String(message || '');
    if (text.includes('[logout-server]') || text.includes('[logout]') || text.includes('[logout-pending]') || text.includes('[logout-cancelled]')) {
        return 'logout';
    }
    if (text.includes('[request-error]') || text.includes('[api-error]') || text.includes('[toast]')) {
        return 'request';
    }
    if (text.includes('[action]')) {
        return 'action';
    }
    if (text.includes('[unhandled-error]') || text.includes('[unhandled-rejection]')) {
        return 'js';
    }
    if (text.includes('[newMessage]')) {
        return 'connection';
    }
    if (text.includes('[hub]') || /UserDataHub|StatusHub|MonitoringHub|DialerHub|WebSocket reconnect|WebSocket disconnected|WebSocket reconnected/i.test(text)) {
        return 'hub';
    }
    if (text.includes('[call]') || /\[ICE\b|JsSIP|\[SIP\b|iceConnectionState|iceGatheringState|call dropped|TURN /i.test(text)) {
        return 'sip';
    }
    return 'console';
}

function normalizeChannel(channel: unknown, message: string): UserConsoleChannel {
    if (typeof channel === 'string' && CONSOLE_CHANNELS.includes(channel as UserConsoleChannel)) {
        return channel as UserConsoleChannel;
    }
    return classifyUserConsoleChannel(message);
}

const buckets = new Map<string, UserBucket>();
const originalConsole: Partial<Record<ConsoleMethod, (...args: any[]) => void>> = {};

let activeKey = ANONYMOUS_USER;
let didBindRealUser = false;
let installed = false;
let recording = false;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let persistQueuedKeys = new Set<string>();

declare global {
    interface Window {
        dumpLogs?: (userName?: string, format?: UserConsoleDumpFormat) => void;
        downloadLogs?: (userName?: string, format?: UserConsoleDumpFormat) => boolean;
        copyLogs?: (userName?: string, format?: UserConsoleDumpFormat) => Promise<boolean>;
        clearLogs?: (userName?: string) => boolean;
    }
}

export function normalizeUserName(userName?: string | null): string {
    const trimmed = String(userName ?? '').trim();
    return trimmed ? trimmed : ANONYMOUS_USER;
}

export function userConsoleStorageKey(userName: string): string {
    return normalizeUserName(userName).toLowerCase();
}

function sanitizeInline(text: string): string {
    return text.replace(SENSITIVE_INLINE, (match, tokenEq, bearer) => {
        if (tokenEq) {
            return `${tokenEq}[userToken]`;
        }
        if (bearer) {
            return `${bearer}[userToken]`;
        }
        return '[userToken]';
    });
}

function serializeValue(value: unknown, seen: WeakSet<object>, depth: number): unknown {
    if (value == null) {
        return value;
    }
    if (typeof value === 'string') {
        return sanitizeInline(value);
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'bigint') {
        return value.toString();
    }
    if (typeof value === 'function') {
        return `[Function ${value.name || 'anonymous'}]`;
    }
    if (value instanceof Error) {
        return {
            name: value.name,
            message: sanitizeInline(value.message),
            stack: value.stack ? sanitizeInline(value.stack) : undefined
        };
    }
    if (typeof Node !== 'undefined' && value instanceof Node) {
        return `[${value.nodeName}]`;
    }
    if (typeof value !== 'object') {
        return String(value);
    }
    if (seen.has(value as object)) {
        return '[Circular]';
    }
    if (depth >= 4) {
        return '[MaxDepth]';
    }

    seen.add(value as object);

    if (Array.isArray(value)) {
        return value.slice(0, 50).map((item) => serializeValue(item, seen, depth + 1));
    }

    const output: Record<string, unknown> = {};
    const entries = Object.entries(value as Record<string, unknown>).slice(0, 50);
    for (const [key, nested] of entries) {
        output[key] = SENSITIVE_KEY.test(key) ? '[userToken]' : serializeValue(nested, seen, depth + 1);
    }
    return output;
}

export function serializeConsoleArgs(args: unknown[], maxChars: number = MAX_ENTRY_CHARS): string {
    const seen = new WeakSet<object>();
    const parts = args.map((arg) => {
        if (typeof arg === 'string') {
            return sanitizeInline(arg);
        }
        try {
            return JSON.stringify(serializeValue(arg, seen, 0));
        } catch {
            try {
                return String(arg);
            } catch {
                return '[Unserializable]';
            }
        }
    });

    const message = parts.join(' ');
    if (message.length <= maxChars) {
        return message;
    }
    return `${message.slice(0, maxChars)}…[truncated]`;
}

function ensureBucket(key: string, displayName?: string): UserBucket {
    const existing = buckets.get(key);
    if (existing) {
        if (displayName && displayName !== ANONYMOUS_USER) {
            existing.userName = displayName;
        }
        return existing;
    }

    const bucket: UserBucket = {
        userName: displayName || key,
        entries: [],
        pinned: [],
        chars: 0
    };
    buckets.set(key, bucket);
    evictExtraUsers(key);
    return bucket;
}

function evictExtraUsers(keepKey: string) {
    while (buckets.size > MAX_STORED_USERS) {
        const removable = Array.from(buckets.keys()).find((key) => key !== keepKey && key !== activeKey);
        if (!removable) {
            break;
        }
        buckets.delete(removable);
        removeStoredUser(removable);
    }
}

function trimBucket(bucket: UserBucket) {
    while (bucket.entries.length > MAX_ENTRIES || bucket.chars > MAX_USER_CHARS) {
        const removed = bucket.entries.shift();
        if (!removed) {
            break;
        }
        bucket.chars -= removed.message.length;
        if (bucket.chars < 0) {
            bucket.chars = 0;
        }
    }
}

function mergeBuckets(fromKey: string, toKey: string, toDisplayName: string) {
    const from = buckets.get(fromKey);
    if (!from || (from.entries.length === 0 && from.pinned.length === 0)) {
        return;
    }

    const to = ensureBucket(toKey, toDisplayName);
    to.pinned = from.pinned.concat(to.pinned);
    while (to.pinned.length > MAX_PINNED_ENTRIES) {
        to.pinned.shift();
    }
    to.entries = from.entries.concat(to.entries);
    to.chars += from.chars;
    trimBucket(to);
    buckets.delete(fromKey);
    removeStoredUser(fromKey);
    queuePersist(toKey);
}

export function setUserConsoleOwner(userName?: string | null) {
    const displayName = normalizeUserName(userName);
    const nextKey = userConsoleStorageKey(displayName);

    if (nextKey === activeKey) {
        ensureBucket(nextKey, displayName);
        return;
    }

    if (!didBindRealUser && activeKey === userConsoleStorageKey(ANONYMOUS_USER) && nextKey !== userConsoleStorageKey(ANONYMOUS_USER)) {
        mergeBuckets(activeKey, nextKey, displayName);
        didBindRealUser = true;
    }

    activeKey = nextKey;
    ensureBucket(nextKey, displayName);

    if (nextKey !== userConsoleStorageKey(ANONYMOUS_USER)) {
        didBindRealUser = true;
    }

    queuePersist(nextKey);
}

export function getActiveUserConsoleKey(): string {
    return activeKey;
}

export function recordUserConsoleMessage(level: UserConsoleLevel, args: unknown[], options?: {pinned?: boolean; replaceGroup?: string; channel?: UserConsoleChannel}) {
    if (recording) {
        return;
    }
    recording = true;
    try {
        const bucket = ensureBucket(activeKey);
        const pinned = !!options?.pinned;
        const group = options?.replaceGroup;
        const message = serializeConsoleArgs(args, pinned ? 32 * 1024 : MAX_ENTRY_CHARS);
        const entry: UserConsoleEntry = {
            ts: new Date().toISOString(),
            level,
            message,
            channel: options?.channel || classifyUserConsoleChannel(message),
            pinned,
            group
        };
        if (pinned) {
            if (group) {
                bucket.pinned = bucket.pinned.filter((item) => item.group !== group);
            }
            bucket.pinned.push(entry);
            while (bucket.pinned.length > MAX_PINNED_ENTRIES) {
                bucket.pinned.shift();
            }
        } else {
            bucket.entries.push(entry);
            bucket.chars += entry.message.length;
            trimBucket(bucket);
        }
        queuePersist(activeKey);
    } finally {
        recording = false;
    }
}

export function recordPinnedUserConsole(level: UserConsoleLevel, args: unknown[]) {
    recordUserConsoleMessage(level, args, {pinned: true, replaceGroup: 'newMessage'});
}

export function flushUserConsolePersist() {
    if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
    }
    persistQueuedKeys.clear();
    buckets.forEach((_, key) => persistUser(key));
    persistIndex();
}

function queuePersist(key: string) {
    persistQueuedKeys.add(key);
    if (persistTimer) {
        return;
    }
    persistTimer = setTimeout(() => {
        persistTimer = null;
        const keys = Array.from(persistQueuedKeys);
        persistQueuedKeys.clear();
        keys.forEach(persistUser);
        persistIndex();
    }, PERSIST_DEBOUNCE_MS);
}

function storageTargets(): Storage[] {
    const targets: Storage[] = [];
    try {
        if (typeof sessionStorage !== 'undefined') {
            targets.push(sessionStorage);
        }
    } catch {
        // ignore blocked storage
    }
    try {
        if (typeof localStorage !== 'undefined') {
            targets.push(localStorage);
        }
    } catch {
        // ignore blocked storage
    }
    return targets;
}

export function runPreservingUserConsoleStorage(mutate: () => void) {
    const saved: Array<{storage: Storage; key: string; value: string}> = [];
    storageTargets().forEach((storage) => {
        try {
            for (let i = 0; i < storage.length; i += 1) {
                const key = storage.key(i);
                if (key && (key.startsWith(STORAGE_PREFIX) || key === INDEX_KEY)) {
                    const value = storage.getItem(key);
                    if (value != null) {
                        saved.push({storage, key, value});
                    }
                }
            }
        } catch {
            // ignore blocked storage
        }
    });
    mutate();
    saved.forEach(({storage, key, value}) => {
        try {
            storage.setItem(key, value);
        } catch {
            // ignore quota
        }
    });
}

function persistUser(key: string) {
    const bucket = buckets.get(key);
    if (!bucket) {
        return;
    }

    const payload = JSON.stringify({
        userName: bucket.userName,
        pinned: bucket.pinned,
        entries: bucket.entries
    });

    storageTargets().forEach((storage) => {
        try {
            storage.setItem(STORAGE_PREFIX + key, payload);
        } catch {
            try {
                storage.removeItem(STORAGE_PREFIX + key);
                storage.setItem(STORAGE_PREFIX + key, payload);
            } catch {
                // quota exceeded — memory buffer still holds logs
            }
        }
    });
}

function persistIndex() {
    const index = JSON.stringify(Array.from(buckets.keys()));
    storageTargets().forEach((storage) => {
        try {
            storage.setItem(INDEX_KEY, index);
        } catch {
            // ignore
        }
    });
}

function removeStoredUser(key: string) {
    storageTargets().forEach((storage) => {
        try {
            storage.removeItem(STORAGE_PREFIX + key);
        } catch {
            // ignore
        }
    });
}

function readStoredIndex(): string[] {
    for (const storage of storageTargets()) {
        try {
            const raw = storage.getItem(INDEX_KEY);
            if (!raw) {
                continue;
            }
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.filter((item) => typeof item === 'string');
            }
        } catch {
            // continue to next storage
        }
    }
    return [];
}

function hydrateFromStorage() {
    const keys = new Set(readStoredIndex());
    storageTargets().forEach((storage) => {
        try {
            for (let i = 0; i < storage.length; i += 1) {
                const name = storage.key(i);
                if (name && name.startsWith(STORAGE_PREFIX)) {
                    keys.add(name.slice(STORAGE_PREFIX.length));
                }
            }
        } catch {
            // ignore
        }
    });

    keys.forEach((key) => {
        if (buckets.has(key)) {
            return;
        }
        for (const storage of storageTargets()) {
            try {
                const raw = storage.getItem(STORAGE_PREFIX + key);
                if (!raw) {
                    continue;
                }
                const parsed = JSON.parse(raw);
                const entries = Array.isArray(parsed?.entries) ? parsed.entries : [];
                const pinned = Array.isArray(parsed?.pinned) ? parsed.pinned : [];
                const bucket = ensureBucket(key, parsed?.userName || key);
                bucket.entries = entries
                    .filter((entry: any) => entry && typeof entry.message === 'string')
                    .map((entry: any) => {
                        const message = String(entry.message).slice(0, MAX_ENTRY_CHARS);
                        return {
                            ts: typeof entry.ts === 'string' ? entry.ts : new Date().toISOString(),
                            level: CONSOLE_METHODS.includes(entry.level) ? entry.level : 'log',
                            message,
                            channel: normalizeChannel(entry.channel, message)
                        };
                    });
                bucket.pinned = pinned
                    .filter((entry: any) => entry && typeof entry.message === 'string')
                    .map((entry: any) => {
                        const message = String(entry.message);
                        return {
                            ts: typeof entry.ts === 'string' ? entry.ts : new Date().toISOString(),
                            level: CONSOLE_METHODS.includes(entry.level) ? entry.level : 'log',
                            message,
                            channel: normalizeChannel(entry.channel, message),
                            pinned: true,
                            group: typeof entry.group === 'string' ? entry.group : undefined
                        };
                    });
                while (bucket.pinned.length > MAX_PINNED_ENTRIES) {
                    bucket.pinned.shift();
                }
                bucket.chars = bucket.entries.reduce((sum: number, entry: UserConsoleEntry) => sum + entry.message.length, 0);
                trimBucket(bucket);
                break;
            } catch {
                // try next storage
            }
        }
    });
}

function findBucket(userName?: string | null): UserBucket | undefined {
    const key = userConsoleStorageKey(userName ?? activeKey);
    return buckets.get(key);
}

export function listUserConsoleUsers(): string[] {
    return Array.from(buckets.values())
        .filter((bucket) => bucket.entries.length > 0 || bucket.pinned.length > 0)
        .map((bucket) => bucket.userName);
}

export function getUserConsoleSnapshot(userName?: string | null): UserConsoleDump | null {
    const bucket = findBucket(userName);
    if (!bucket || (bucket.entries.length === 0 && bucket.pinned.length === 0)) {
        return null;
    }

    return {
        userName: bucket.userName,
        exportedAt: new Date().toISOString(),
        appVersion: Version,
        href: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        pinned: bucket.pinned.slice(),
        entries: bucket.entries.slice(),
        truncated: bucket.entries.length >= MAX_ENTRIES || bucket.chars >= MAX_USER_CHARS
    };
}

function formatDumpEntries(entries: UserConsoleEntry[]): string {
    if (!entries.length) {
        return '(empty)\n';
    }
    return `${entries.map((entry) => `[${entry.ts}] [${entry.level.toUpperCase()}] ${entry.message}`).join('\n')}\n`;
}

export function groupUserConsoleEntriesByChannel(entries: UserConsoleEntry[]): Record<UserConsoleChannel, UserConsoleEntry[]> {
    const grouped = CONSOLE_CHANNELS.reduce((acc, channel) => {
        acc[channel] = [];
        return acc;
    }, {} as Record<UserConsoleChannel, UserConsoleEntry[]>);

    entries.forEach((entry) => {
        grouped[normalizeChannel(entry.channel, entry.message)].push(entry);
    });
    return grouped;
}

function formatChannelSummary(grouped: Record<UserConsoleChannel, UserConsoleEntry[]>): string {
    return CHANNEL_SECTIONS
        .map((section) => `${section.id}=${grouped[section.id].length}`)
        .join('  ');
}

function formatChannelSections(grouped: Record<UserConsoleChannel, UserConsoleEntry[]>, divider: string): string[] {
    return CHANNEL_SECTIONS.flatMap((section) => {
        const entries = grouped[section.id];
        if (!entries.length) {
            return [];
        }
        return [
            divider,
            ` ${section.title}`,
            ` records: ${entries.length}`,
            divider,
            formatDumpEntries(entries).trimEnd(),
            ''
        ];
    });
}

function formatDumpHeader(dump: UserConsoleDump, format: UserConsoleDumpFormat, extraLines: string[] = []): string[] {
    const divider = '================================================================================';
    const grouped = groupUserConsoleEntriesByChannel(dump.entries);
    return [
        divider,
        ' CALLCENTER USER CONSOLE DUMP',
        divider,
        ` user: ${dump.userName}`,
        ` exportedAt: ${dump.exportedAt}`,
        ` appVersion: ${dump.appVersion}`,
        ` href: ${dump.href}`,
        ` userAgent: ${dump.userAgent}`,
        ` format: ${format}`,
        ` channels: ${formatChannelSummary(grouped)}`,
        ...extraLines
    ];
}

export function formatUserConsoleDump(dump: UserConsoleDump, format: UserConsoleDumpFormat = 'channels'): string {
    return format === 'time' ? formatUserConsoleDumpByTime(dump) : formatUserConsoleDumpByChannel(dump);
}

export function formatUserConsoleDumpByChannel(dump: UserConsoleDump): string {
    const divider = '================================================================================';
    const grouped = groupUserConsoleEntriesByChannel(dump.entries);
    return [
        ...formatDumpHeader(dump, 'channels'),
        '',
        divider,
        ' DEFAULT DATA  |  connection / newMessage  |  not counted in ring',
        ` records: ${dump.pinned.length}`,
        divider,
        formatDumpEntries(dump.pinned).trimEnd(),
        '',
        ...formatChannelSections(grouped, divider)
    ].join('\n');
}

export function formatUserConsoleDumpByTime(dump: UserConsoleDump): string {
    const divider = '================================================================================';
    return [
        ...formatDumpHeader(dump, 'time'),
        '',
        divider,
        ' DEFAULT DATA  |  connection / newMessage  |  not counted in ring',
        ` records: ${dump.pinned.length}`,
        divider,
        formatDumpEntries(dump.pinned).trimEnd(),
        '',
        divider,
        ' EVENTS BY TIME',
        ` records: ${dump.entries.length}`,
        divider,
        formatDumpEntries(dump.entries).trimEnd(),
        ''
    ].join('\n');
}

export function buildUserConsoleFileName(userName: string, format: UserConsoleDumpFormat = 'channels'): string {
    const safe = normalizeUserName(userName).replace(/[^a-zA-Z0-9._-]+/g, '_');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const kind = format === 'time' ? 'time' : 'blocks';
    return `console-${safe}-${stamp}-${kind}.txt`;
}

export function downloadTextFile(fileName: string, content: string) {
    const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function downloadUserConsoleLogs(userName?: string | null, format: UserConsoleDumpFormat = 'channels'): boolean {
    const dump = getUserConsoleSnapshot(userName);
    if (!dump) {
        return false;
    }
    downloadTextFile(buildUserConsoleFileName(dump.userName, format), formatUserConsoleDump(dump, format));
    return true;
}

export function downloadBothUserConsoleLogs(userName?: string | null): boolean {
    const dump = getUserConsoleSnapshot(userName);
    if (!dump) {
        return false;
    }
    downloadTextFile(buildUserConsoleFileName(dump.userName, 'channels'), formatUserConsoleDump(dump, 'channels'));
    downloadTextFile(buildUserConsoleFileName(dump.userName, 'time'), formatUserConsoleDump(dump, 'time'));
    return true;
}

export async function copyUserConsoleLogs(userName?: string | null, format: UserConsoleDumpFormat = 'channels'): Promise<boolean> {
    const dump = getUserConsoleSnapshot(userName);
    if (!dump) {
        return false;
    }
    const text = formatUserConsoleDump(dump, format);
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

export function printUserConsoleLogs(userName?: string | null, format: UserConsoleDumpFormat = 'channels') {
    const dump = getUserConsoleSnapshot(userName);
    const logger = originalConsole.log || console.log;
    if (!dump) {
        logger.call(console, '[console] no logs for', userName || activeKey);
        return;
    }
    logger.call(console, formatUserConsoleDump(dump, format));
}

export function clearUserConsoleLogs(userName?: string | null): boolean {
    const key = userConsoleStorageKey(userName ?? activeKey);
    const bucket = buckets.get(key);
    const hadData = !!bucket && (bucket.entries.length > 0 || bucket.pinned.length > 0);
    if (bucket) {
        bucket.entries = [];
        bucket.pinned = [];
        bucket.chars = 0;
    }
    removeStoredUser(key);
    persistIndex();
    return hadData;
}

function attachWindowHelpers() {
    if (typeof window === 'undefined') {
        return;
    }
    window.dumpLogs = printUserConsoleLogs;
    window.downloadLogs = downloadUserConsoleLogs;
    window.copyLogs = copyUserConsoleLogs;
    window.clearLogs = clearUserConsoleLogs;
}

export function installUserConsoleCapture() {
    if (installed || typeof console === 'undefined') {
        return;
    }

    hydrateFromStorage();
    ensureBucket(activeKey, ANONYMOUS_USER);

    CONSOLE_METHODS.forEach((method) => {
        const original = console[method];
        if (typeof original !== 'function') {
            return;
        }
        originalConsole[method] = original.bind(console);
        console[method] = (...args: any[]) => {
            recordUserConsoleMessage(method, args);
            originalConsole[method]?.(...args);
        };
    });

    attachWindowHelpers();
    installed = true;
}

export function bindUserConsoleStore(store: {getState: () => any; subscribe: (listener: () => void) => () => void}) {
    const sync = () => {
        const userName = store.getState()?.UsersReducer?.userData?.userName;
        setUserConsoleOwner(typeof userName === 'string' ? userName : null);
    };
    sync();
    return store.subscribe(sync);
}

export function resetUserConsoleLogsForTests() {
    if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
    }
    persistQueuedKeys.clear();
    buckets.clear();
    activeKey = ANONYMOUS_USER;
    didBindRealUser = false;
    recording = false;

    if (installed) {
        CONSOLE_METHODS.forEach((method) => {
            const original = originalConsole[method];
            if (original) {
                console[method] = original;
            }
        });
        installed = false;
    }

    storageTargets().forEach((storage) => {
        const toRemove: string[] = [];
        try {
            for (let i = 0; i < storage.length; i += 1) {
                const name = storage.key(i);
                if (name && (name.startsWith(STORAGE_PREFIX) || name === INDEX_KEY)) {
                    toRemove.push(name);
                }
            }
            toRemove.forEach((name) => storage.removeItem(name));
        } catch {
            // ignore
        }
    });
}
